import React from 'react';
import { Modal, ModalContent, Button, Snippet, ModalHeader, ModalBody, ModalFooter } from '@heroui/react';
import { ResponsiveModal, Typography } from '../../../components';
import { Icon } from '@iconify/react';
import { useTranslations } from '../../../contexts/translation.context';

interface WithdrawalSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  withdrawalData: {
    amount: number;
    created_at: string;
    address: string;
  } | null;
}

const WithdrawalSuccessModal: React.FC<WithdrawalSuccessModalProps> = ({
  isOpen,
  onClose,
  withdrawalData,
}) => {
  const { t } = useTranslations();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}.${month}.${year}, ${hours}:${minutes}`;
  };

  const handleCopyAddress = () => {
    if (withdrawalData?.address) {
      navigator.clipboard.writeText(withdrawalData.address);
      // TODO: Показать уведомление о копировании
    }
  };

  if (!withdrawalData) return null;

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      classNames={{
        base: 'md:max-w-[34.5625rem]',
        backdrop: 'bg-black/50',
      }}
    >
      <ModalContent className="rounded-4 p-0">
        {/* Header with icon and title */}
        <ModalHeader className="flex flex-col gap-3 pt-6 px-4 md:px-6 pb-4">
          <div className="flex items-center gap-2">
            <div className="relative size-8 shrink-0">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="size-7 rounded-full bg-successDeep/20" />
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Icon
                  icon="solar:check-circle-bold"
                  width={20}
                  height={20}
                  className="text-successDeep"
                />
              </div>
            </div>
            <Typography variant="h6">{t('wallet.withdrawalSuccess.title')}</Typography>
          </div>
        </ModalHeader>

        {/* Content */}
        <ModalBody className="px-4 md:px-6 py-3">
          <div className="border border-zinc-200 rounded-5 p-4">
            {/* Amount Row */}
            <div className="flex items-center gap-6 h-[2.375rem] px-2 py-4">
              <Typography
                variant="body2"
                className="!text-auth-muted !font-medium !text-sm w-[7.1875rem]"
              >
                {t('wallet.withdrawalSuccess.amount')}
              </Typography>
              <Typography variant="body2" className="font-medium text-auth-ink">
                ${withdrawalData.amount.toFixed(2)}
              </Typography>
            </div>

            {/* Date Row */}
            <div className="flex items-center gap-6 h-[2.375rem] px-2 py-4">
              <Typography
                variant="body2"
                className="!text-auth-muted !font-medium !text-sm w-[7.1875rem]"
              >
                {t('wallet.withdrawalSuccess.createdAt')}
              </Typography>
              <Typography variant="body2" className="font-medium text-auth-ink">
                {formatDate(withdrawalData.created_at)}
              </Typography>
            </div>

            {/* Address Row */}
            <div className="flex items-center gap-6 h-[2.375rem] px-2 py-4">
              <Typography
                variant="body2"
                className="!text-auth-muted !font-medium !text-sm whitespace-nowrap"
              >
                {t('wallet.withdrawalSuccess.walletAddress')}
              </Typography>
              <Snippet
                symbol={null}
                tooltipProps={{
                  color: 'foreground',
                  content: 'Скопировать адрес',
                  disableAnimation: true,
                  placement: 'right',
                  closeDelay: 0,
                }}
                classNames={{
                  base: 'bg-husky rounded-5 px-2 gap-0 py-0.5 w-fit',
                  pre: 'bg-transparent text-partner text-sm max-w-[18.75rem] truncate font-mono border-none',
                  copyButton: 'text-partner !min-w-[1.375rem] !max-w-4 h-4 hover:text-partner',
                  checkIcon: 'text-partner ',
                }}
              >
                {withdrawalData.address}
              </Snippet>
            </div>
          </div>
        </ModalBody>

        {/* Footer */}
        <ModalFooter className="flex items-center justify-end gap-2 px-4 md:px-6 py-4">
          <Button className="bg-partner h-10 px-3.5 py-2.5 rounded-3" onPress={onClose}>
            <Typography variant="body2" className="!text-white font-semibold">
              {t('wallet.withdrawalSuccess.close')}
            </Typography>
          </Button>
        </ModalFooter>
      </ModalContent>
    </ResponsiveModal>
  );
};

export default WithdrawalSuccessModal;
